import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RingState, MAX_VISIBLE_RINGS, TierName } from '../challenge.model';
import { motifPathById } from './motif-library';

interface RenderedRing {
  state: RingState;
  radius: number;
  ringSvg: SafeHtml;
}

@Component({
    selector: 'app-mandala',
    templateUrl: './mandala.component.html',
    styleUrls: ['./mandala.component.scss'],
    standalone: false
})
export class MandalaComponent implements OnChanges {
  @Input() rings: RingState[] = [];
  @Input() pulse: { category: string; at: number } | null = null;
  @Input() tierUp: { category: string; tier: TierName; at: number } | null = null;

  /** Viewbox is fixed; the host element scales the SVG via CSS. */
  readonly viewBox = 240;
  readonly center = 120;

  rendered: RenderedRing[] = [];
  pulseCategory: string | null = null;
  tierUpCategory: string | null = null;
  tierUpLabel: string | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rings']) this.recompute();
    if (changes['pulse']) {
      this.pulseCategory = this.pulse?.category ?? null;
    }
    if (changes['tierUp']) {
      this.tierUpCategory = this.tierUp?.category ?? null;
      this.tierUpLabel = this.tierUp ? this.formatTier(this.tierUp.tier) : null;
    }
  }

  private formatTier(t: TierName): string {
    return t.charAt(0).toUpperCase() + t.slice(1) + '!';
  }

  isDimmed(r: RenderedRing): boolean {
    return this.tierUpCategory !== null && this.tierUpCategory !== r.state.category;
  }

  isLevelingUp(r: RenderedRing): boolean {
    return this.tierUpCategory === r.state.category;
  }

  trackByCategory(_: number, r: RenderedRing) { return r.state.category; }

  isPulsing(r: RenderedRing): boolean {
    return this.pulseCategory === r.state.category;
  }

  private recompute() {
    if (!this.rings.length) { this.rendered = []; return; }
    const ringCount = Math.min(this.rings.length, MAX_VISIBLE_RINGS);
    const inner = 28, outer = 96;
    this.rendered = this.rings.slice(0, ringCount).map((state, i) => {
      const radius = ringCount === 1 ? (inner + outer) / 2 : inner + ((outer - inner) * i) / (ringCount - 1);
      return {
        state,
        radius,
        ringSvg: this.sanitizer.bypassSecurityTrustHtml(this.buildRingSvg(state, radius)),
      };
    });
  }

  private buildRingSvg(state: RingState, radius: number): string {
    const pieces: string[] = [];

    // 1. Soft halo behind the ring outline (always on).
    pieces.push(
      `<g filter="url(#mandala-halo)" opacity="0.5">
         <circle cx="0" cy="0" r="${radius}" fill="none" stroke="${state.color}" stroke-width="3"/>
       </g>`
    );

    // 2. Thin sharp outline ring.
    pieces.push(
      `<circle cx="0" cy="0" r="${radius}" fill="none" stroke="${state.color}" stroke-width="0.8" opacity="0.85" stroke-dasharray="3,2"/>`
    );

    // 3. One petal per habit slot — shell when not done today, solid when done.
    const slots = state.slots ?? [];
    const count = slots.length;
    if (count > 0) {
      const motifSize = Math.max(2.5, radius * 0.12);
      const petalClass = state.petalsSpin ? 'petal-axis spinning' : 'petal-axis';
      const petals: string[] = [];
      for (let k = 0; k < count; k++) {
        const angleDeg = (360 / count) * k;
        const slot = slots[k];
        const motifFragment = motifPathById(state.motif, motifSize, state.color, slot.completedToday);
        // Outer <g> places the petal at its angle on the ring; inner <g class="petal-axis">
        // is the unit that spins on its own center when petalsSpin is on.
        petals.push(
          `<g transform="rotate(${angleDeg}) translate(0 -${radius})"><g class="${petalClass}">${motifFragment}</g></g>`
        );
      }
      pieces.push(`<g>${petals.join('')}</g>`);
    }

    const ringClass = state.ringRotates ? 'ring-group rotating' : 'ring-group';
    return `<g class="${ringClass}"><g class="ring-content">${pieces.join('')}</g></g>`;
  }
}
